import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";

export const GallerySection = () => {
  const navigate = useNavigate();

  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Slider Modal
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.rigve.com/api/getgallery?type=0",
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === 1) {
          setGalleryData(data?.data?.data || []);
        } else {
          throw new Error("Failed to fetch gallery data");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  const galleryItems =
    galleryData?.map((item, index) => ({
      id: item.id,
      imageUrl: item.image,
      type: item.type,
      title: `Elegant Surface ${index + 1}`,
      category: "Premium Texture",
      className:
        index === 0
          ? "md:col-span-2 md:row-span-2"
          : index === 3
            ? "md:row-span-2"
            : "",
    })) || [];

  const nextSlide = () => {
    setSelectedIndex((prev) =>
      prev === galleryItems.length - 1 ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setSelectedIndex((prev) =>
      prev === 0 ? galleryItems.length - 1 : prev - 1,
    );
  };

  const closeModal = () => {
    setSelectedIndex(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;

      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedIndex, galleryItems.length]);

  if (loading) {
    return (
      <div className="py-20 text-center text-gray-500">
        Loading premium gallery...
      </div>
    );
  }

  if (error) {
    return <div className="py-20 text-center text-red-500">{error}</div>;
  }

  return (
    <>
      <section id="gallery" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="fs-21 font-bold text-gray-900 leading-tight">
              Premium Texture{" "}
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-transparent bg-clip-text">
                {" "}
                Gallery
              </span>
            </h1>

            <div className="w-24 h-1 bg-orange-500 mx-auto my-4 rounded-full"></div>

            <p className="text-gray-600 text-lg">
              Explore our exclusive collection of modern textures, surfaces, and
              finishes crafted for timeless architectural and interior designs.
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[250px]">
            {galleryItems.map((item, index) => (
              <div
                key={item.id}
                onClick={() => setSelectedIndex(index)}
                className={`relative rounded-2xl overflow-hidden group cursor-pointer ${item.className}`}
              >
                {item.type === 1 ? (
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                  />
                ) : (
                  <video
                    src={item.imageUrl}
                    muted
                    autoPlay
                    loop
                    playsInline
                    className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                  />
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />

                {/* Zoom Icon */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition duration-300">
                  <div className="bg-white/20 backdrop-blur-md p-2 rounded-full">
                    <ZoomIn className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Text */}
                <div className="absolute bottom-5 left-5 text-white opacity-0 group-hover:opacity-100 transition duration-500">
                  <p className="text-sm text-orange-300">{item.category}</p>
                  {/* <h3 className="text-xl font-semibold">{item.title}</h3> */}
                </div>
              </div>
            ))}
          </div>

          {/* Button */}
          <div className="text-center mt-16">
            <button
              onClick={() => navigate("/gallery")}
              className="px-10 py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-full text-lg shadow-lg hover:scale-105 transition-transform"
            >
              Explore Full Gallery →
            </button>
          </div>
        </div>
      </section>

      {/* Fullscreen Gallery Modal */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={closeModal}
        >
          {/* Close */}
          <button
            onClick={closeModal}
            className="absolute top-6 right-6 z-50 bg-white/10 hover:bg-white/20 backdrop-blur-md p-2 rounded-full text-white"
          >
            <X size={24} />
          </button>
          {/* Previous */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            className="absolute left-3 md:left-8 z-50 bg-white/10 hover:bg-white/20 backdrop-blur-md p-2 rounded-full text-white"
          >
            <ChevronLeft size={24} />
          </button>
          {/* Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            className="absolute right-3 md:right-8 z-50 bg-white/10 hover:bg-white/20 backdrop-blur-md p-2 rounded-full text-white"
          >
            <ChevronRight size={24} />
          </button>
          {/* Media */}
          <div
            className="w-full max-w-7xl px-4 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {galleryItems[selectedIndex]?.type === 1 ? (
              <img
                src={galleryItems[selectedIndex]?.imageUrl}
                alt={galleryItems[selectedIndex]?.title}
                className="max-w-full max-h-[90vh] object-contain rounded-xl"
              />
            ) : (
              <video
                src={galleryItems[selectedIndex]?.imageUrl}
                controls
                autoPlay
                className="max-w-full max-h-[90vh] rounded-xl"
              />
            )}
          </div>{" "}
        </div>
      )}
    </>
  );
};
